import React, { memo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ConfirmationFormLayout,
  ConfirmationFormWrapper,
  ContactLine,
} from "../confirmation/confirmation-contact-form/confirmation-contact-form";
import { ConfirmationLayoutWrapper } from "../confirmation/confirmation-layout";
import { GolContactNameField } from "../confirmation/confirmation-contact-form/gol-contact-name-field";
import { GolContactFormContext } from "../confirmation/confirmation-contact-form/gol-contact-form-interfaces";
import { ContinueButton } from "../continue-button/continue-button";
import { subscribeToNewsletter } from "../../requests/subscribe-to-newsletter";
import { contactStore } from "../../stores/contact.store";
import { Contact } from "../../types";
import { Loader } from "../loader/loader";
import { ErrorMessage } from "../errors/message-error-view";
import { GolCheckField } from "../confirmation/confirmation-contact-form/gol-check-field";
import { Title } from "../title/title";
import styled from "styled-components";
import { InformationText } from "../information-text/information-text";
import { setDangerouslyInnerHtml } from "../../helpers/set-inner-html";
import { moduleSettings } from "../../stores/module-settings.store";
import { getLanguageSwitcherService } from "../../services/language-switcher.service";
import { last } from "cypress/types/lodash";

const SuccessWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: auto;
`;

const NewsletterFormWrapper = styled(ConfirmationFormWrapper)`
background-color: ${(props) => props.theme.backgroundColor};
border: 1px solid #F8F8F8;
box-sizing: border-box;
border-radius: ${(props) =>
    props.theme.isBackground ? "0 8px 8px 8px" : "8px"};
box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
`;

const _NewsletterForm = () => {
  const [submitSubscription, setSubmitSubscription] = useState(false);
  const [messageError, setMessageError] = useState("");
  const [subscriptionSucceed, setSubscriptionSucceed] = useState(false);
  const [subscriptionFailed, setSubscriptionFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldsInErrors, setFieldsInErrors] = useState<string[]>([]);
  const [policy, setPolicy] = useState("");
  const { t, i18n } = useTranslation();
  const form = useRef({
    firstname: undefined,
    lastname: undefined,
    email: undefined,
    restaurant_id: parseInt(moduleSettings.getRestaurantId()),
  });
  useEffect(() => {
    const localeUrl = getLanguageSwitcherService().golLocaleFromSwitcher(
      i18n.language
    );
    setPolicy(`${moduleSettings.privacyPolicyUrl}&locale=${localeUrl}`);
  }, [i18n.language]);

  useEffect(() => {
    if (submitSubscription) {
      setLoading(true);
      const {
        firstname,
        lastname,
        email,
        restaurant_id,
      } = form.current;
      contactStore.contact = { firstname: firstname ?? "", lastname: lastname ?? "", email: email ?? "", restaurant_id } as Contact
      const subscription = subscribeToNewsletter().subscribe({
        next: (resp: Contact) => {
          contactStore.contact = resp;
          setSubscriptionSucceed(true);
          setSubscriptionFailed(false);
          setLoading(false);
        },
        error: (error) => {
          setSubscriptionSucceed(false);
          setLoading(false);
          setSubscriptionFailed(true);
          setMessageError(error.response.data["error"]);
        },
      });
      return () => {
        subscription.unsubscribe();
      };
    }
  }, [submitSubscription]);
  const requiredFields = ["lastname", "firstname", "email", "terms"];

  const handleFormChange = (event: {
    target: { name: any; value: any; checked: any; valueAsDate: any };
  }) => {
    const { name, value, checked } = event.target;
    let defaultValue = value || undefined;
    // Update state
    // Assign new value to the appropriate form field
    form.current = {
      ...form.current,
      [name]: checked ? checked : defaultValue,
    };

    if ((defaultValue && defaultValue.length) || checked) {
      const array = [...fieldsInErrors];
      const index = array.indexOf(name);
      if (index !== -1) {
        array.splice(index, 1);
        setFieldsInErrors(Array.from(new Set(array)));
      }
    }
  };

  const fieldFilled = (field: string) => {
    let value = form.current[field];
    if ("terms" === field) {
      return value;
    }
    return typeof value !== "undefined" && value != null;
  };

  const checkFormValidity = () => {
    let updatedFieldsInErrors = [...fieldsInErrors, ...requiredFieldsFilled()];
    setFieldsInErrors(Array.from(new Set(updatedFieldsInErrors)));
    if (updatedFieldsInErrors.length === 0) {
      setSubmitSubscription(true);
    } else {
      setSubmitSubscription(false);
    }
  };

  const rightEmailFormat = (value: string) => {
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value);
  };

  const requiredFieldsFilled = () => {
    let requiredFieldsInError: string[] = [];
    requiredFields.forEach((field) => {
      if (!fieldFilled(field)) {
        requiredFieldsInError.push(field);
      } else if (field === "email") {
        if (!rightEmailFormat(form.current[field]!)) {
          requiredFieldsInError.push(field);
        }
      }
    });
    return requiredFieldsInError;
  };

  const value = {
    form: form.current,
    handleFormChange,
    fieldsInErrors,
    requiredFields,
  };
  if (loading) {
    return <Loader position={"fixed"} />;
  }

  if (subscriptionSucceed) {
    return (
      <SuccessWrapper>
        <InformationText
          text={t("instabook.newsletter.success")}
          status="success"
        />
      </SuccessWrapper>
    );
  }

  return (
    <>
      <ConfirmationLayoutWrapper>
        <ConfirmationFormLayout>
          <Title
            text={t("instabook.newsletter.title")}
            extraText={t("instabook.newsletter.summary")}
          />
          {subscriptionFailed && (
            <ErrorMessage
              error={
                messageError
                  ? t("instabook.newsletter.error", messageError)
                  : t("instabook.errors.error")
              }
              status={"error"}
            />
          )}
          <NewsletterFormWrapper minHeight={"130px"}>
            <GolContactFormContext.Provider value={value}>
              <ContactLine
                data-test="firstname-lastname"
                className="golFlewDirectionResponsiveMode"
              >
                <GolContactNameField
                  name={"firstname"}
                  clazz="contactField golFlexOne"
                />
                <GolContactNameField
                  name={"lastname"}
                  clazz="contactField golFlexOne"
                />
              </ContactLine>
              <ContactLine
                data-test="email"
                className="golFlewDirectionResponsiveMode"
              >
                <GolContactNameField
                  clazz="contactField golFlexOne"
                  name={"email"}
                />
              </ContactLine>
              <GolCheckField
                name="terms"
                label={
                  <span
                    dangerouslySetInnerHTML={setDangerouslyInnerHtml(
                      t,
                      "instabook.newsletter.terms",
                      {
                        policy,
                      }
                    )}
                  />
                }
                controlName="terms"
                controlId={"terms"}
              />
            </GolContactFormContext.Provider>
          </NewsletterFormWrapper>
          <ContinueButton
            buttonTextKey={t("instabook.newsletter.validate")}
            onClick={checkFormValidity}
          />
        </ConfirmationFormLayout>
      </ConfirmationLayoutWrapper>
    </>
  );
};

export const NewsletterForm = memo(_NewsletterForm);
